export interface FetchCommentsParameters {
  apiUrl: string;
  apiKey?: string;
  commentParams?: CommentParams;
}

export type CommentParams = SetCommentParams | DeleteOrDeactivateParams;

export interface SetCommentParams {
  parentCommentId?: string;
  subscribe?: string;
  name?: string;
  email?: string;
  message?: string;
}

export interface DeleteOrDeactivateParams {
  commentId: string;
}

/**
 * Returns the URL with query parameters for the given API endpoint.
 * @returns {string} The URL with query parameters.
 */
export function getUrlWithParams(apiUrl: string, commentParams?: CommentParams) {
  const url = new URL(apiUrl);
  const urlSearchParams = new URLSearchParams(url.search);

  // Add comment data to URL search params
  if (commentParams && Object.keys(commentParams).length > 0) {
    Object.keys(commentParams).forEach((key) => {
      urlSearchParams.append(key, commentParams[key]);
    });
  }

  url.search = urlSearchParams.toString();

  return url.toString();
}

export function handleErrors(response) {
  if (!response.ok) {
    throw new Error(response.statusText);
  }
}

/**
 * Fetches the comments from the API using the provided parameters.
 * @param {CommentParams} parameters.commentParams The comment parameters.
 * @returns {Promise<Object>} A promise that resolves to the comments data.
 */
export async function fetchCommentsData({ apiUrl, apiKey, commentParams }: FetchCommentsParameters) {
  const url = getUrlWithParams(apiUrl, commentParams);
  let data;
  try {
    const response = await fetch(url, {
      headers: {
        ...(apiKey !== undefined && { 'x-api-key': apiKey }),
      },
    });
    if (!response.ok) {
      data = handleErrors(response);
    }
    data = await response.json();
  } catch (error) {
    console.error(error);
  }

  return data;
}

export async function addComment({ apiUrl, apiKey, commentParams }: FetchCommentsParameters) {
  const url = getUrlWithParams(apiUrl, commentParams);
  let data;
  try {
    const response = await fetch(url, {
      headers: {
        ...(apiKey !== undefined && { 'x-api-key': apiKey }),
      },
    });
    if (!response.ok) {
      data = handleErrors(response);
    }
    data = await response.json();
  } catch (error) {
    console.error(error);
  }

  return data;
}

export async function deleteOrDeactivateComment({ apiUrl, commentParams }: FetchCommentsParameters) {
  const url = getUrlWithParams(apiUrl, commentParams);
  let data;
  try {
    const response = await fetch(url);
    if (!response.ok) {
      data = handleErrors(response);
    }
    data = await response.json();
  } catch (error) {
    console.error(error);
  }

  return data;
}
